import { Button, DropdownGroup, DropdownMenu } from '@toggle/design-system';
import { Condition, ConditionType } from '@toggle/toggle';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSnakeMeta } from '~/react-query-hooks/snakes/useSnake';
import { mapDisplayFormatToUnit } from '~/utils/currency/currency';

import { ConditionAssetSearch } from '../condition-asset-search/ConditionAssetSearch';
import { ConditionDataList } from '../condition-data-list/ConditionDataList';
import styles from './CustomScenarioModal.module.scss';
import * as S from './CustomScenarioModal.styles';

export type ConditionBuilderProps = {
  snake: string;
  onConditionChange: (condition: Partial<Condition>) => void;
  defaultCondition?: Condition;
};

export const ConditionBuilder = ({
  snake,
  onConditionChange,
  defaultCondition,
}: ConditionBuilderProps) => {
  const { t } = useTranslation('screener');
  const [condition, setCondition] = useState<Partial<Condition>>(
    defaultCondition ?? {
      condition: ConditionType.Above,
    }
  );

  const { data: snakeMeta } = useSnakeMeta(snake);

  const inputUnit = mapDisplayFormatToUnit(
    snakeMeta?.display_format,
    snakeMeta?.currency
  );

  useEffect(() => {
    onConditionChange(condition);
  }, [condition]);

  const renderNumberInput = (
    propertyName: 'value' | 'min_value' | 'max_value' | 'duration',
    suffix?: string
  ) => {
    const typedValue = condition[propertyName] ?? '';
    return (
      <S.StyledInput
        data-testid="input-value"
        type="number"
        value={typedValue}
        onChange={e => {
          const nextValue = e.target.valueAsNumber;

          setCondition(prev => ({
            ...prev,
            [propertyName]: isNaN(nextValue) ? undefined : nextValue,
          }));
        }}
        rightIcon={
          <S.InputSuffix data-testid="input-unit">{suffix}</S.InputSuffix>
        }
      />
    );
  };

  const renderDurationFields = (suffix: string) => {
    return (
      <>
        {renderNumberInput('value', suffix)}
        <S.OptionLabel>{t('scenario:condition.in')}</S.OptionLabel>

        {renderNumberInput('duration')}
        <DropdownMenu
          data-testid="duration-unit-input"
          triggerNode={
            <Button
              className={styles.option}
              /* i18next-extract-mark-context-next-line  ["d", "w" , "m", "y"]  */
              label={t('scenario:condition.duration', {
                context: condition.duration_unit,
              })}
              iconName="ChevronLightDown"
              variant="empty"
              iconPosition="right"
            />
          }
        >
          <DropdownGroup
            items={['d', 'w', 'm', 'y'].map(d => ({
              /* i18next-extract-mark-context-next-line  ["d", "w" , "m", "y"]  */
              label: t('scenario:condition.duration', {
                context: d,
              }),
              key: d,
              onClick: () =>
                setCondition(prev => ({
                  ...prev,
                  duration_unit: d,
                })),
            }))}
          />
        </DropdownMenu>
      </>
    );
  };

  const renderConditionFields = (suffix: string) => {
    switch (condition.condition) {
      case ConditionType.Above:
      case ConditionType.Below:
        return renderNumberInput('value', suffix);
      case ConditionType.CrossesAbove:
      case ConditionType.CrossesBelow:
        return renderNumberInput('value', suffix);
      case ConditionType.RisesBy:
      case ConditionType.FallsBy:
        return renderDurationFields('%');
      case ConditionType.RisesByLevel:
      case ConditionType.FallsByLevel:
        return renderDurationFields(suffix);
      default:
        return (
          <>
            {renderNumberInput('min_value', suffix)}
            <S.OptionLabel>{t('scenario:condition.and')}</S.OptionLabel>
            {renderNumberInput('max_value', suffix)}
          </>
        );
    }
  };

  return (
    <S.Root>
      {defaultCondition && (
        <>
          <ConditionAssetSearch
            condition={defaultCondition}
            response={onConditionChange}
          />
          <ConditionDataList
            condition={defaultCondition}
            response={onConditionChange}
          />
        </>
      )}
      <DropdownMenu
        triggerNode={
          <Button
            className={styles.option}
            /* i18next-extract-mark-context-next-line  ["crosses_above", "crosses_below", "crosses_below", "below", "between",  "rises_by", "falls_by", "rises_by_level",  "falls_by_level"] */
            label={t('scenario:condition.type', {
              context: condition.condition,
            })}
            iconName="ChevronLightDown"
            variant="empty"
            iconPosition="right"
          />
        }
      >
        <DropdownGroup
          items={Object.values(ConditionType).map(c => ({
            /* i18next-extract-mark-context-next-line  ["crosses_above", "crosses_below", "crosses_below", "below", "between",  "rises_by", "falls_by", "rises_by_level",  "falls_by_level"] */
            label: t('scenario:condition.type', {
              context: c,
            }),
            key: c,
            onClick: () => {
              setCondition({ condition: c, duration_unit: 'd' });
            },
          }))}
        />
      </DropdownMenu>
      {renderConditionFields(inputUnit)}
    </S.Root>
  );
};
